import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useMutation,
} from "@tanstack/react-query";
import { AdminViewsService, BillingService, type UsersNeedingAction, type CrmUser, type GetApiCrmPeriodDetailsResponse, type GetApiLabVisitCostBreakdownByIdResponse } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll, fetchAllByIds } from "./fetchAll";
import { crmSearchFilters } from "@/lib/utils/crmSearch";
import { customerFilterQuery, sortCustomers, type CustomerFilters, type CustomerSortKey, type CustomerSortDir } from "@/pages/crm/shared/customer-table-data";

// Re-export generated type. After openapi sync this will update automatically.
export type CrmPeriodDetails = GetApiCrmPeriodDetailsResponse;

export const useLabPriceSources = () =>
  useQuery({
    queryKey: ["lab-price-sources"],
    queryFn: async () => {
      const res = await AdminViewsService.getApiLabPriceSources({
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch lab price sources");
      return res.data as { sources: string[] };
    },
    staleTime: 30 * 60_000,
  });

export const useUsersNeedingAction = () =>
  useQuery({
    queryKey: ["users-needing-action"],
    queryFn: () => fetchAll<UsersNeedingAction>(AdminViewsService.getApiUsersNeedingAction),
    staleTime: 60_000,
  });

/**
 * One patient's actionable visits. The whole worklist is a single scan of the
 * view, so a screen that only cares about one patient should not ask for it.
 */
export const useUserNeedingAction = (authUserId: string | null | undefined) =>
  useQuery({
    queryKey: ["users-needing-action", authUserId],
    queryFn: () =>
      fetchAll<UsersNeedingAction>((opts) =>
        AdminViewsService.getApiUsersNeedingAction({
          ...opts,
          query: { ...opts.query, "filter[authUserId]": authUserId! },
        }),
      ),
    enabled: !!authUserId,
    staleTime: 60_000,
  });

export const CRM_TABLE_PAGE_SIZE = 200;

/**
 * The CRM table, one server-ordered page at a time. `order` has to be a
 * total order — the table appends `authUserId` as a tiebreaker — or offset
 * pages on a view with duplicate sort keys would overlap.
 */
export const useCrmUsersPaged = (order: string, filters?: CustomerFilters, enabled = true) =>
  useInfiniteQuery({
    queryKey: ["crm-users", "paged", order, filters],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await AdminViewsService.getApiCrmUser({
        headers: getAuthHeaders(),
        query: { ...(filters ? customerFilterQuery(filters) : {}), order, limit: CRM_TABLE_PAGE_SIZE, offset: pageParam },
        querySerializer: { array: { style: "form", explode: true } },
      });
      if (!Array.isArray(res.data))
        throw new Error("Failed to fetch CRM users");
      return res.data as CrmUser[];
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < CRM_TABLE_PAGE_SIZE
        ? undefined
        : pages.length * CRM_TABLE_PAGE_SIZE,
    enabled,
    staleTime: 5 * 60_000,
  });

export const useCrmUserCount = () =>
  useQuery({
    queryKey: ["crm-user-count"],
    queryFn: async () => {
      const res = await AdminViewsService.getApiCrmUserCount({
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch CRM user count");
      // The generated type flattens single-property responses (see
      // useLabPriceSources); the wire shape is { count }.
      return (res.data as unknown as { count: number }).count;
    },
    staleTime: 5 * 60_000,
  });

/**
 * CRM rows for a known set of users, in `in:` batches that stay inside the
 * request-size limit. Filtering the view still aggregates every user before
 * the WHERE applies (see #538), but only these rows cross the wire.
 */
export const useCrmUsersByIds = (authUserIds: string[]) => {
  const ids = [...new Set(authUserIds)].sort();
  return useQuery({
    queryKey: ["crm-users", "byIds", ids],
    queryFn: () =>
      ids.length === 0
        ? Promise.resolve([] as CrmUser[])
        : fetchAllByIds<CrmUser>(ids, (batch, opts) =>
            AdminViewsService.getApiCrmUser({
              ...opts,
              query: {
                ...opts.query,
                "filter[authUserId]": `in:${batch.join(",")}`,
              },
            }),
          ),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
};

/** Roster search on the server; `crmSearchFilters` builds the patterns. */
export const useCrmUserSearch = (term: string, filters?: CustomerFilters, enabled = true) => {
  const q = term.trim().toLowerCase();
  return useQuery({
    queryKey: ["crm-users", "search", q, filters],
    queryFn: async () => {
      const pages = await Promise.all(
        crmSearchFilters(q).map(([column, value]) =>
          fetchAll<CrmUser>(
            (opts) =>
              AdminViewsService.getApiCrmUser({
                ...opts,
                query: {
                  ...opts.query,
                  ...(filters ? customerFilterQuery(filters) : {}),
                  order: "authUserId.asc",
                  [column]: value,
                },
                querySerializer: { array: { style: "form", explode: true } },
              }),
            1000,
          ),
        ),
      );
      const byUser = new Map<string, CrmUser>();
      for (const u of pages.flat()) if (u.authUserId) byUser.set(u.authUserId, u);
      return [...byUser.values()];
    },
    enabled: enabled && q.length >= 2,
    staleTime: 60_000,
  });
};

export const useCrmSubscriptionTiers = () => useQuery({
  queryKey: ["crm-subscription-tiers"],
  queryFn: async () => {
    const res = await AdminViewsService.getApiCrmSubscriptionTiers({ headers: getAuthHeaders() });
    if (!res.data) throw new Error("Kunne ikke hente abonnementstyper");
    return res.data;
  },
  staleTime: 5 * 60_000,
});

/** Fetch every matching row, not merely the infinite table's loaded pages. */
export const useExportCrmCustomers = () => useMutation({
  mutationFn: async ({ filters, search, sortKey, sortDir }: {
    filters: CustomerFilters; search: string; sortKey: CustomerSortKey; sortDir: CustomerSortDir;
  }) => {
    const query = customerFilterQuery(filters);
    const q = search.trim().toLowerCase();
    const searches = q.length >= 2 ? crmSearchFilters(q) : [null];
    const pages = await Promise.all(searches.map((searchFilter) => fetchAll<CrmUser>((opts) =>
      AdminViewsService.getApiCrmUser({
        ...opts,
        query: { ...opts.query, ...query, order: `${sortKey}.${sortDir},authUserId.asc`,
          ...(searchFilter ? { [searchFilter[0]]: searchFilter[1] } : {}) },
        querySerializer: { array: { style: "form", explode: true } },
      }),
    )));
    const unique = [...new Map(pages.flat().map((row) => [row.authUserId, row])).values()];
    // Search results are a union; match the table's local ordering. Otherwise
    // retain the database order rather than re-sorting under browser collation.
    return q.length >= 2 ? sortCustomers(unique, sortKey, sortDir) : unique;
  },
});

/**
 * One patient's fødselsnummer. It is not on the crm_user rows any more — the
 * backend serves it from a separate endpoint that writes an audit_log row per
 * read, so this only fires once the admin asks for it (`enabled`) and never
 * refetches a value already on screen.
 */
export const useCrmUserNin = (authUserId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["crm-user-nin", authUserId],
    queryFn: async () => {
      const res = await AdminViewsService.getApiCrmUserByIdNin({
        path: { id: authUserId! },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch NIN");
      return (res.data as unknown as { nin: string | null }).nin;
    },
    enabled: enabled && !!authUserId,
    staleTime: Infinity,
    retry: false,
  });

export const useCrmPeriodStats = (from: string, to: string, priceSource?: string, includeCompanyVisits?: boolean, enabled = true) =>
  useQuery({
    queryKey: ["crm-period-stats", from, to, priceSource, includeCompanyVisits],
    queryFn: async () => {
      const res = await AdminViewsService.getApiCrmPeriodStats({
        query: { from, to, priceSource, includeCompanyVisits },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch period stats");
      return res.data;
    },
    enabled: enabled && !!from && !!to,
    staleTime: 5 * 60_000,
  });

export const useCrmPeriodDetails = (from: string, to: string, priceSource?: string, includeCompanyVisits?: boolean, enabled = true) =>
  useQuery({
    queryKey: ["crm-period-details", from, to, priceSource, includeCompanyVisits],
    queryFn: async () => {
      const res = await AdminViewsService.getApiCrmPeriodDetails({
        query: { from, to, priceSource, includeCompanyVisits },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch period details");
      return res.data as CrmPeriodDetails;
    },
    enabled: enabled && !!from && !!to,
    staleTime: 5 * 60_000,
  });

export type LabVisitCostBreakdown = GetApiLabVisitCostBreakdownByIdResponse;

export const useLabVisitCostBreakdown = (labVisitId: string | null, priceSource?: string) =>
  useQuery({
    queryKey: ["lab-visit-cost-breakdown", labVisitId, priceSource],
    queryFn: async () => {
      const res = await AdminViewsService.getApiLabVisitCostBreakdownById({
        path: { id: labVisitId! },
        query: { priceSource },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch cost breakdown");
      return res.data as LabVisitCostBreakdown;
    },
    enabled: !!labVisitId,
    staleTime: 5 * 60_000,
  });

export const usePredictedIncome = (from: string, to: string) =>
  useQuery({
    queryKey: ["predicted-income", from, to],
    queryFn: async () => {
      const res = await BillingService.getApiBillingPredictedIncome({
        query: { from, to },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to fetch predicted income");
      return res.data as unknown as import("@/lib/api_client/types.gen").GetApiBillingPredictedIncomeResponse;
    },
    enabled: !!from && !!to,
    staleTime: 30 * 60_000,
  });
